import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Archive, Check, ChevronRight, Clock3, Copy, FileDiff, Gauge, History,
  Loader2, Plus, RefreshCw, Save, ShieldCheck, Sparkles,
} from 'lucide-react';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { PermissionGate } from '../components';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import limitsApi from './limitsApi';
import {
  CATEGORY_LABELS, formatValue, formatWindow, templateDiff, templateLimitRows,
  templateLimitsPayload, templateRef,
} from './limitPresentation';

const templateRows = (payload) => Array.isArray(payload)
  ? payload
  : payload?.templates || payload?.items || [];

// Actions target immutable/draft VERSION ids. A logical template id repeats
// across every version and therefore cannot identify a selected row.
const templateId = (template) => template.version_id || template.id || template.template_id;
const templateStatus = (template) => (template.status || (template.published_at ? 'published' : 'draft')).toLowerCase();
const statusClass = (status) => ({
  published: 'border-success/30 bg-success/10 text-success',
  draft: 'border-warning/40 bg-warning/10 text-warning-text',
  archived: 'border-border bg-muted text-muted-foreground',
})[status] || 'border-border bg-muted text-muted-foreground';

const LimitsTemplatesPage = () => {
  const { getAdminToken } = useAdminAuth();
  const [templates, setTemplates] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draftValues, setDraftValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [action, setAction] = useState(null);
  const [actionForm, setActionForm] = useState({ name: '' });
  const [compareId, setCompareId] = useState('');
  const [serverDiff, setServerDiff] = useState(null);

  const token = useCallback(() => getAdminToken(), [getAdminToken]);

  const loadTemplates = useCallback(async (preserveId) => {
    setLoading(true);
    setError(null);
    try {
      const [data, catalogData] = await Promise.all([
        limitsApi.listTemplates(token()), limitsApi.getCatalog(token()),
      ]);
      const rows = templateRows(data);
      setTemplates(rows);
      setCatalog(catalogData);
      setSelectedId((current) => preserveId || current || templateId(rows.find((t) => t.is_default)) || templateId(rows[0]) || null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Limits templates could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    if (!selectedId) { setSelected(null); return; }
    const value = templates.find((row) => templateId(row) === selectedId) || null;
    setDetailLoading(false);
    setSelected(value);
    setDraftValues(Object.fromEntries(
      templateLimitRows(value, catalog).map((q) => [q.key, q.value ?? '']),
    ));
  }, [selectedId, templates, catalog]);

  const published = templates.filter((t) => templateStatus(t) === 'published');
  const draft = selected && templateStatus(selected) === 'draft';
  const selectedLimits = useMemo(
    () => templateLimitRows(selected, catalog), [selected, catalog],
  );
  const groupedLimits = useMemo(() => selectedLimits.reduce((groups, row) => {
    const category = row.category || 'other';
    (groups[category] = groups[category] || []).push(row);
    return groups;
  }, {}), [selectedLimits]);

  const openAction = (name) => {
    setAction(name);
    setActionForm({
      name: name === 'clone' ? `${selected?.name || 'Limits template'} copy` : '',
    });
  };

  const closeAction = () => { setAction(null); setActionForm({ name: '' }); };

  const submitAction = async () => {
    if (['new', 'clone'].includes(action) && !actionForm.name.trim()) return;
    setBusy(action);
    try {
      let result;
      if (action === 'new') result = await limitsApi.createDraft(token(), { name: actionForm.name.trim(), description: '' });
      if (action === 'clone') result = await limitsApi.cloneTemplate(token(), selectedId, { name: actionForm.name.trim() });
      if (action === 'publish') result = await limitsApi.publishTemplate(token(), selectedId, { expected_revision: Number(selected.revision || 1) });
      if (action === 'default') result = await limitsApi.setDefaultTemplate(token(), selectedId, {});
      if (action === 'archive') result = await limitsApi.archiveTemplate(token(), selectedId, {});
      const nextId = templateId(result?.item || result?.template || result) || selectedId;
      toast.success({ new: 'Draft created', clone: 'Template cloned', publish: 'Immutable version published', default: 'Default template updated', archive: 'Template archived' }[action]);
      closeAction();
      await loadTemplates(nextId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'The template change could not be saved.');
    } finally {
      setBusy(null);
    }
  };

  const saveDraft = async () => {
    setBusy('save');
    try {
      const payload = {
        expected_revision: Number(selected.revision || 1),
        limits: templateLimitsPayload(selectedLimits, draftValues),
      };
      const data = await limitsApi.updateDraft(token(), selectedId, payload);
      setSelected(data.item || data.template || data);
      toast.success('Draft saved');
      await loadTemplates(selectedId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Draft could not be saved.');
    } finally {
      setBusy(null);
    }
  };

  const compare = async () => {
    if (!compareId) return;
    setBusy('compare');
    try {
      const data = await limitsApi.compareTemplates(token(), compareId, selectedId);
      setServerDiff(data.changes || data.diff || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Versions could not be compared.');
    } finally {
      setBusy(null);
    }
  };

  const comparison = serverDiff || templateDiff(
    templates.find((t) => templateId(t) === compareId), selected,
  ).filter((row) => row.changed);

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="limits-templates-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-xl bg-primary/10 p-2 text-primary"><Gauge className="h-5 w-5" /></span>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Limits &amp; Templates</h1>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                Define what future tenants receive. Published versions are immutable; existing tenants stay pinned until an explicit migration.
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => loadTemplates(selectedId)} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <PermissionGate permission="quota:write">
            <Button onClick={() => openAction('new')}><Plus className="mr-2 h-4 w-4" />New draft</Button>
          </PermissionGate>
        </div>
      </div>

      <Card className="border-primary/20 bg-gradient-to-r from-primary/10 to-card">
        <CardContent className="grid gap-4 py-5 sm:grid-cols-3">
          <div><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Published versions</p><p className="mt-1 text-2xl font-semibold">{published.length}</p></div>
          <div><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Current default</p><p className="mt-1 font-semibold text-foreground">{templateRef(templates.find((t) => t.is_default) || {})}</p></div>
          <div><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Provisioning rule</p><p className="mt-1 text-sm text-foreground">New tenants only; no silent migration</p></div>
        </CardContent>
      </Card>

      {error ? (
        <Card className="border-destructive/40"><CardContent className="py-10 text-center"><p className="font-medium text-destructive-text">{error}</p><Button className="mt-4" variant="outline" onClick={() => loadTemplates()}>Try again</Button></CardContent></Card>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
          <Card className="h-fit">
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4" />Version history</CardTitle><CardDescription>Select a draft or immutable version.</CardDescription></CardHeader>
            <CardContent className="space-y-2">
              {loading ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : templates.map((tpl) => {
                const id = templateId(tpl); const status = templateStatus(tpl);
                return (
                  <button key={id} type="button" onClick={() => { setSelectedId(id); setServerDiff(null); }} className={`w-full rounded-xl border p-3 text-left transition ${selectedId === id ? 'border-primary/50 bg-primary/10 ring-1 ring-primary/20' : 'border-border hover:border-primary/40'}`}>
                    <div className="flex items-start justify-between gap-2"><p className="text-sm font-semibold text-foreground">{templateRef(tpl)}</p><ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /></div>
                    <div className="mt-2 flex flex-wrap gap-1.5"><Badge variant="outline" className={statusClass(status)}>{status}</Badge>{tpl.is_default && <Badge className="bg-primary text-primary-foreground"><Check className="mr-1 h-3 w-3" />Default</Badge>}</div>
                    <p className="mt-2 text-xs text-muted-foreground">{tpl.updated_at || tpl.published_at ? new Date(tpl.updated_at || tpl.published_at).toLocaleString() : 'Not published'}</p>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          <div className="min-w-0 space-y-5">
            {detailLoading || !selected ? (
              <Card><CardContent className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></CardContent></Card>
            ) : (
              <>
                <Card>
                  <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div><div className="flex flex-wrap items-center gap-2"><CardTitle>{templateRef(selected)}</CardTitle><Badge variant="outline" className={statusClass(templateStatus(selected))}>{templateStatus(selected)}</Badge>{selected.is_default && <Badge>Default for new tenants</Badge>}</div><CardDescription className="mt-2">{selected.description || `${selectedLimits.length} catalogue limits in this version.`}</CardDescription></div>
                    <div className="flex flex-wrap gap-2">
                      <PermissionGate permission="quota:write"><Button size="sm" variant="outline" onClick={() => openAction('clone')}><Copy className="mr-2 h-4 w-4" />Clone</Button></PermissionGate>
                      {draft && <PermissionGate permission="quota:write"><Button size="sm" variant="outline" onClick={saveDraft} disabled={busy === 'save'}>{busy === 'save' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save draft</Button></PermissionGate>}
                      {draft && <PermissionGate permission="quota:write"><Button size="sm" onClick={() => openAction('publish')}><Sparkles className="mr-2 h-4 w-4" />Publish version</Button></PermissionGate>}
                      {templateStatus(selected) === 'published' && !selected.is_default && <PermissionGate permission="quota:write"><Button size="sm" onClick={() => openAction('default')}><ShieldCheck className="mr-2 h-4 w-4" />Select default</Button></PermissionGate>}
                      {templateStatus(selected) === 'published' && !selected.is_default && <PermissionGate permission="quota:write"><Button size="sm" variant="outline" onClick={() => openAction('archive')}><Archive className="mr-2 h-4 w-4" />Archive</Button></PermissionGate>}
                    </div>
                  </CardHeader>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-base">Compare versions</CardTitle><CardDescription>Review every change before selecting a default or planning a tenant migration.</CardDescription></CardHeader>
                  <CardContent>
                    <div className="flex flex-col gap-2 sm:flex-row"><Select value={compareId} onValueChange={(value) => { setCompareId(value); setServerDiff(null); }}><SelectTrigger className="sm:max-w-sm"><SelectValue placeholder="Choose an older version" /></SelectTrigger><SelectContent>{templates.filter((t) => templateId(t) !== selectedId).map((t) => <SelectItem key={templateId(t)} value={templateId(t)}>{templateRef(t)}</SelectItem>)}</SelectContent></Select><Button variant="outline" onClick={compare} disabled={!compareId || busy === 'compare'}>{busy === 'compare' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDiff className="mr-2 h-4 w-4" />}Compare</Button></div>
                    {compareId && <div className="mt-4 overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Limit</TableHead><TableHead>Before</TableHead><TableHead>Selected version</TableHead></TableRow></TableHeader><TableBody>{comparison.length ? comparison.map((row) => <TableRow key={row.key}><TableCell><p className="font-medium">{row.name || row.quota_key || row.key}</p><p className="font-mono text-xs text-muted-foreground">{row.quota_key || row.key}</p></TableCell><TableCell>{formatValue(row.before ?? row.old_value ?? row.left, row.unit)}</TableCell><TableCell className="font-semibold">{formatValue(row.after ?? row.new_value ?? row.right, row.unit)}</TableCell></TableRow>) : <TableRow><TableCell colSpan={3} className="py-8 text-center text-muted-foreground">No changed limits.</TableCell></TableRow>}</TableBody></Table></div>}
                  </CardContent>
                </Card>

                {Object.entries(groupedLimits).map(([category, rows]) => (
                  <Card key={category}>
                    <CardHeader><CardTitle className="text-base">{CATEGORY_LABELS[category] || category}</CardTitle><CardDescription>{rows.length} limit{rows.length === 1 ? '' : 's'} · scope and reset policy remain part of the catalogue definition.</CardDescription></CardHeader>
                    <CardContent className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Limit</TableHead><TableHead>Scope</TableHead><TableHead>Window</TableHead><TableHead>Value</TableHead><TableHead>Runtime status</TableHead></TableRow></TableHeader><TableBody>{rows.map((row) => { const key = row.quota_key || row.key; const value = draftValues[key] ?? row.value ?? row.limit; const runtimeStatus = row.runtime_status || row.enforcement_status || row.enforcement_mode || 'declared_only'; return <TableRow key={key}><TableCell><p className="font-medium text-foreground">{row.name || row.label || key}</p><p className="font-mono text-xs text-muted-foreground">{key}</p></TableCell><TableCell className="capitalize">{row.scope || 'tenant'}</TableCell><TableCell>{formatWindow(row.window || row.period)}</TableCell><TableCell>{draft ? <Input className="h-9 w-32" type="number" min={-1} value={value} onChange={(event) => setDraftValues((current) => ({ ...current, [key]: event.target.value }))} aria-label={`${row.name || row.label || key} value`} /> : <span className="font-semibold tabular-nums">{formatValue(value, row.unit)}</span>}</TableCell><TableCell><Badge variant="outline" className="capitalize">{runtimeStatus.replaceAll('_', ' ')}</Badge></TableCell></TableRow>; })}</TableBody></Table></CardContent>
                  </Card>
                ))}

                <Card className="border-border bg-muted/50"><CardContent className="flex gap-3 py-4 text-sm text-muted-foreground"><Clock3 className="mt-0.5 h-4 w-4 shrink-0" /><p>Publishing makes the selected draft immutable. It never rewrites tenants pinned to an older version. Moving existing tenants requires a separate previewed and audited migration.</p></CardContent></Card>
              </>
            )}
          </div>
        </div>
      )}

      <Dialog open={!!action} onOpenChange={(open) => !open && closeAction()}>
        <DialogContent>
          <DialogHeader><DialogTitle>{{ new: 'Create limits draft', clone: 'Clone template', publish: 'Publish immutable version', default: 'Use for new tenants', archive: 'Archive template' }[action]}</DialogTitle><DialogDescription>{{ new: 'Start a new template family from the current catalogue defaults.', clone: 'Create an editable draft from this selected version without changing it.', publish: 'After publication, this version cannot be edited. Existing tenants remain pinned.', default: 'Only tenants created after this change receive this version.', archive: 'Archived versions remain readable for tenant provenance and audit.' }[action]}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            {['new', 'clone'].includes(action) && <div className="space-y-2"><Label htmlFor="template-name">Template name</Label><Input id="template-name" value={actionForm.name} onChange={(e) => setActionForm((f) => ({ ...f, name: e.target.value }))} /></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={closeAction} disabled={!!busy}>Cancel</Button><Button onClick={submitAction} disabled={!!busy || (['new', 'clone'].includes(action) && !actionForm.name.trim())}>{busy === action && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{{ new: 'Create draft', clone: 'Clone', publish: 'Publish', default: 'Select default', archive: 'Archive' }[action]}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LimitsTemplatesPage;
